import { Image, Skeleton, Modal, Popconfirm } from "antd"
import { ReactNode, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlay } from "@fortawesome/pro-solid-svg-icons/faPlay"
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons/faTrashCan"
import { VisitAttachment } from "./scheduleApi"
import { useGetVisitAttachmentsQuery, useDeleteAttachmentMutation } from "./scheduleQueries"
import { isVideo, visitPhotosOf } from "./visitPhotosModel"
import { useInvalidateVisits } from "../../domain/jobs/jobsQueries"

type VisitPhotosProps = {
  visitId: string
  emptyState?: ReactNode
  errorState?: ReactNode
  jobId?: number
}

type DeleteButtonProps = {
  onDelete: () => void
}

const DeleteButton = ({ onDelete }: DeleteButtonProps) => (
  <div
    style={{
      position: 'absolute',
      top: '12px',
      right: '12px',
      zIndex: 10
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <Popconfirm
      title="Are you sure you want to delete this attachment?"
      onConfirm={onDelete}
      okText="Yes"
      cancelText="No"
    >
      <div
        style={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
      >
        <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: '18px', color: '#ff4d4f' }} />
      </div>
    </Popconfirm>
  </div>
)

const VisitPhotos = ({ visitId, emptyState, errorState, jobId }: VisitPhotosProps) => {
  const { data: attachments, isLoading, isError } = useGetVisitAttachmentsQuery(visitId)
  const { mutate: deleteAttachment } = useDeleteAttachmentMutation(visitId)
  const invalidateVisits = useInvalidateVisits()
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState<VisitAttachment | null>(null)

  const attachmentsWithThumbnails = visitPhotosOf(attachments)
  const skeletonCount = attachmentsWithThumbnails.length || 2

  const handleVideoClick = (attachment: VisitAttachment) => {
    setSelectedVideo(attachment)
    setVideoModalOpen(true)
  }

  const handleDelete = (attachment: VisitAttachment) => {
    deleteAttachment(attachment.id, {
      onSuccess: () => {
        const visitJobId = jobId ?? attachment.jobId
        if (visitJobId) {
          invalidateVisits(visitJobId)
        }
      }
    })
  }

  if (isLoading) {
    return (
      <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton.Image key={i} active style={{ width: '100%', height: 'auto', aspectRatio: '1' }} />
        ))}
      </div>
    )
  }

  if (isError && !attachments) {
    return <>{errorState ?? null}</>
  }

  if (attachmentsWithThumbnails.length === 0) {
    return <>{emptyState ?? null}</>
  }

  const images = attachmentsWithThumbnails.filter(a => !isVideo(a))
  const videos = attachmentsWithThumbnails.filter(a => isVideo(a))

  return (
    <div style={{ marginTop: '16px' }}>
      <Image.PreviewGroup>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {images.map(attachment => (
            <div key={attachment.id} style={{ position: 'relative', aspectRatio: '1' }}>
              <Image
                src={attachment.thumbnail!}
                alt={attachment.name}
                width="100%"
                height="100%"
                style={{
                  objectFit: 'cover',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
                wrapperStyle={{
                  width: '100%',
                  height: '100%'
                }}
                preview={{
                  src: attachment.url
                }}
              />
              <DeleteButton onDelete={() => handleDelete(attachment)} />
            </div>
          ))}
          {videos.map(attachment => (
            <div
              key={attachment.id}
              style={{
                position: 'relative',
                aspectRatio: '1',
                cursor: 'pointer'
              }}
              onClick={() => handleVideoClick(attachment)}
            >
              <img
                src={attachment.thumbnail!}
                alt={attachment.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px'
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '8px'
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <FontAwesomeIcon icon={faPlay} style={{ fontSize: '16px', color: '#333', marginLeft: '2px' }} />
                </div>
              </div>
              <DeleteButton onDelete={() => handleDelete(attachment)} />
            </div>
          ))}
        </div>
      </Image.PreviewGroup>
      <Modal
        open={videoModalOpen}
        onCancel={() => setVideoModalOpen(false)}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        {selectedVideo && (
          <video
            src={selectedVideo.url}
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '70vh' }}
          />
        )}
      </Modal>
    </div>
  )
}

export default VisitPhotos
